import { Navigate, Route, Routes } from 'react-router';
import { useTicker } from '@/application/timer/useTicker';
import { useAppUpdater, useOutboxSyncDriver } from '@/application/container';
import { useAppUpdateWatch } from '@/application/update/useAppUpdate';
import { useOutboxReplay } from '@/application/outbox/useOutboxSync';
import { QueueScreen } from '@/features/queue/QueueScreen';
import { JobScreen } from '@/features/queue/JobScreen';
import { ScanScreen } from '@/features/scan/ScanScreen';
import { ProfileScreen } from '@/features/people/ProfileScreen';
import { IntakeScreen } from '@/features/intake/IntakeScreen';
import { PrintSheetScreen } from '@/features/print/PrintSheet';
import { OrdersScreen } from '@/features/orders/OrdersScreen';
import { OnHoldScreen } from '@/features/payments/OnHoldScreen';
import { PaymentsScreen } from '@/features/payments/PaymentsScreen';
import { PaymentSummaryScreen } from '@/features/payments/PaymentSummaryScreen';
import { DoctorStatementScreen } from '@/features/payments/DoctorStatementScreen';
import { ShippingScreen } from '@/features/shipping/ShippingScreen';
import { FinalCheckScreen } from '@/features/qc/FinalCheckScreen';
import { QcCaseScreen } from '@/features/qc/QcCaseScreen';
import { ArchiveScreen } from '@/features/archive/ArchiveScreen';
import { CaseRecordScreen } from '@/features/archive/CaseRecordScreen';
import { EditCaseScreen } from '@/features/orders/EditCaseScreen';
import { ReportsScreen } from '@/features/reports/ReportsScreen';
import { ActivityScreen } from '@/features/activity/ActivityScreen';
import { UserRecordScreen } from '@/features/admin/UserRecordScreen';
import { UsersScreen } from '@/features/admin/UsersScreen';
import { SettingsScreen } from '@/features/admin/SettingsScreen';
import { ItemsScreen } from '@/features/items/ItemsScreen';
import { DoctorsScreen } from '@/features/clients/DoctorsScreen';
import { DoctorProfileScreen } from '@/features/clients/DoctorProfileScreen';
import { PatientsScreen } from '@/features/clients/PatientsScreen';
import { PatientProfileScreen } from '@/features/clients/PatientProfileScreen';
import { OverviewScreen } from '@/features/floor/OverviewScreen';
import { DashboardScreen } from '@/features/dashboard/DashboardScreen';
import { LineScreen } from '@/features/floor/LineScreen';
import { LogisticsScreen } from '@/features/floor/LogisticsScreen';
import { StageScreen } from '@/features/floor/StageScreen';
import { StaffScreen } from '@/features/people/StaffScreen';
import { PerfScreen } from '@/features/people/PerfScreen';
import { ConfirmHost } from '@/ui/primitives/ConfirmHost';
import { Toast } from '@/ui/primitives/Toast';
import { HAPTICS_SETTING, setHapticsEnabled } from '@/ui/motion/haptics';
import { useDeviceSetting } from '@/application/usecases/useDeviceSetting';
import { env } from '@/shared/config/env';
import { ErrorBoundary } from './ErrorBoundary';
import { LoginRoute } from './LoginRoute';
import { ShellLayout } from './ShellLayout';

export function App() {
  // Section 10 item 4: one interval for the whole application, mounted once.
  useTicker();
  // Section 9 rule 3, mounted once for the same reason: reconnect, focus and a
  // slow sweep all drive one replay rather than one per screen.
  useOutboxReplay(useOutboxSyncDriver());
  // A newer build on the server: launch, back in the foreground, and every five
  // minutes. Not in development, which has no /version.json, nor in the mock
  // build, whose worker is MSW's.
  useAppUpdateWatch(useAppUpdater(), env.appVersion, env.isProduction && !env.useMocks);
  // Vibration on confirmations, as this device last chose. Section 5, 5 October 2026.
  const [haptics] = useDeviceSetting(HAPTICS_SETTING, true);
  setHapticsEnabled(haptics);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route element={<ShellLayout />}>
          <Route
            path="/queue"
            element={
              <ErrorBoundary>
                <QueueScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/queue/:kitId"
            element={
              <ErrorBoundary>
                <JobScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/scan"
            element={
              <ErrorBoundary>
                <ScanScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders"
            element={
              <ErrorBoundary>
                <OrdersScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders/:jo"
            element={
              <ErrorBoundary>
                <OrdersScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/payments"
            element={
              <ErrorBoundary>
                <PaymentsScreen />
              </ErrorBoundary>
            }
          />
          {/* The money of one case, and of one doctor. The request of 8 October 2026 (fourth). */}
          <Route
            path="/payments/case/:jo"
            element={
              <ErrorBoundary>
                <PaymentSummaryScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/payments/doctor/:id"
            element={
              <ErrorBoundary>
                <DoctorStatementScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/on-hold"
            element={
              <ErrorBoundary>
                <OnHoldScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/shipping"
            element={
              <ErrorBoundary>
                <ShippingScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/qc"
            element={
              <ErrorBoundary>
                <FinalCheckScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/qc/:kitId"
            element={
              <ErrorBoundary>
                <QcCaseScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/archive"
            element={
              <ErrorBoundary>
                <ArchiveScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/archive/:jo"
            element={
              <ErrorBoundary>
                <CaseRecordScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders/:jo/record"
            element={
              <ErrorBoundary>
                <CaseRecordScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders/:jo/edit"
            element={
              <ErrorBoundary>
                <EditCaseScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/reports"
            element={
              <ErrorBoundary>
                <ReportsScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/intake"
            element={
              <ErrorBoundary>
                <IntakeScreen />
              </ErrorBoundary>
            }
          />
          {/*
            The print sheet is a full page overlay rather than a screen in the
            shell, because @media print hides every sibling of #print-root and
            the shell would otherwise have to be excluded by hand.
          */}
          <Route
            path="/print/:kitId"
            element={
              <ErrorBoundary>
                <PrintSheetScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/activity"
            element={
              <ErrorBoundary>
                <ActivityScreen />
              </ErrorBoundary>
            }
          />
          {/* Everybody's own, and anybody's for management. Request of 4 October 2026 (second), section 7. */}
          <Route
            path="/profile"
            element={
              <ErrorBoundary>
                <ProfileScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <ErrorBoundary>
                <ProfileScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/users"
            element={
              <ErrorBoundary>
                <UsersScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <ErrorBoundary>
                <UserRecordScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/settings"
            element={
              <ErrorBoundary>
                <SettingsScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/items"
            element={
              <ErrorBoundary>
                <ItemsScreen />
              </ErrorBoundary>
            }
          />
          {/*
            Doctors and patients: each a list, with a record's profile under it.
            Guarded by the `doctors` and `patients` views in routes.ts, a profile
            by its list's. The request of 6 October 2026 (second), item 4.
          */}
          <Route
            path="/doctors"
            element={
              <ErrorBoundary>
                <DoctorsScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/doctors/:id"
            element={
              <ErrorBoundary>
                <DoctorProfileScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/patients"
            element={
              <ErrorBoundary>
                <PatientsScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/patients/:id"
            element={
              <ErrorBoundary>
                <PatientProfileScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <OverviewScreen />
              </ErrorBoundary>
            }
          />
          {/* A person's own page of widgets. The lab's request of 12 October 2026. */}
          <Route
            path="/dashboard"
            element={
              <ErrorBoundary>
                <DashboardScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/line"
            element={
              <ErrorBoundary>
                <LineScreen />
              </ErrorBoundary>
            }
          />
          {/* Before /line/:stage: logistics is a list of orders, not a bench. */}
          <Route
            path="/line/logistics"
            element={
              <ErrorBoundary>
                <LogisticsScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/line/:stage"
            element={
              <ErrorBoundary>
                <StageScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/staff"
            element={
              <ErrorBoundary>
                <StaffScreen />
              </ErrorBoundary>
            }
          />
          <Route
            path="/perf"
            element={
              <ErrorBoundary>
                <PerfScreen />
              </ErrorBoundary>
            }
          />
        </Route>
        {/* An unknown path is a stale bookmark, not an error worth a page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/*
        The question before an action, for the whole app. After the routes, so it
        lies over any dialog, sheet or drawer it is asked from. The request of
        5 October 2026 (third), section 2.
      */}
      <ConfirmHost />
      <Toast />
    </>
  );
}
